import React, { useState, useEffect } from "react"
import styled from "@emotion/styled"
import { keyframes } from "@emotion/core"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"

import SEO from "../components/seo"
import Layout from "../components/layout"
import Navbar from "../components/navbar"
import ButtonLink from "../components/ButtonLink"
import SectionHeading from "../components/SectionHeading"
import icons from "../images/icons.svg"
import commonStyles from "../lib/commonStyles"

const Section = styled.section`
  padding: 75px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${props =>
    props.active
      ? "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 34.8%,rgb(235, 235, 235) 34.8%)"
      : "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 59.5%,rgb(235, 235, 235) 59.5%)"};

  @media (max-width: 1350px) {
    background: ${props =>
      props.active
        ? "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 33.7%,rgb(235, 235, 235) 33.7%)"
        : "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 59.1%,rgb(235, 235, 235) 59.1%)"};
  }

  @media (max-width: 950px) {
    background: ${props =>
      props.active
        ? "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 34.8%,rgb(235, 235, 235) 34.8%)"
        : "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 38%,rgb(235, 235, 235) 38%)"};
  }

  @media (max-width: 480px) {
    background: ${props =>
      props.active
        ? "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 34.8%,rgb(235, 235, 235) 34.8%)"
        : "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 21.7%,rgb(235, 235, 235) 21.7%)"};
  }

  @media (max-width: 475px) {
    background: ${props =>
      props.active
        ? "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 34.8%,rgb(235, 235, 235) 34.8%)"
        : "linear-gradient(180deg,rgb(255, 255, 255) 0%,rgb(255, 255, 255) 24.1%,rgb(235, 235, 235) 24.1%)"};
  }
`

const BoxList = styled.ul`
  display: grid;
  gap: 30px;
  grid-template: 1fr / repeat(4, 1fr);
  margin-bottom: 75px;

  @media (max-width: 950px) {
    grid-template: repeat(2, 1fr) / repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template: repeat(4, 1fr) / 1fr;
  }
`
const BoxItem = styled.li`
  width: 300px;
  height: 500px;
  position: relative;
  cursor: pointer;

  @media (max-width: 1350px) {
    width: 250px;
    height: 450px;
  }

  @media (max-width: 1150px) {
    width: 200px;
    height: 400px;
  }

  @media (max-width: 950px) {
    width: 300px;
    height: 500px;
  }

  @media (max-width: 700px) {
    width: 250px;
    height: 450px;
  }

  @media (max-width: 580px) {
    width: 200px;
    height: 400px;
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 500px;
  }

  @media (max-width: 340px) {
    width: 250px;
    height: 450px;
  }
`

const Overlay = styled.div`
  background-color: rgba(38, 154, 140, 0.6);
  width: ${props => (props.selected ? "100%" : "calc(100% - 12px)")};
  height: ${props =>
    props.selected ? "calc(32.3% + 140px)" : "calc(32.3% - 6px)"};
  position: absolute;
  bottom: 0;
  left: 0;
  transform: ${props =>
    props.selected ? "translate(0, 140px)" : "translate(6px, -6px)"};
  transition: all 0.3s ease;
`
const Figure = styled.figure`
  width: 100%;
  height: 100%;
  border: 6px solid white;
  position: relative;
`
const FigureImg = styled(props => <Img {...props} />)`
  height: 100%;
`
const Caption = styled.figcaption`
  font-family: ${commonStyles.fonts.sans};
  color: white;
  position: absolute;
  bottom: ${props => (props.selected ? "-108px" : "90px")};
  left: 20px;
  z-index: 10;
  font-size: ${props => (props.selected ? "28px" : "20px")};
  width: ${props => (props.selected ? "168px" : "131px")};
  transition: all 0.3s ease;

  @media (max-width: 1350px) {
    bottom: ${props => (props.selected ? "-108px" : "70px")};
  }

  @media (max-width: 1150px) {
    bottom: ${props => (props.selected ? "-108px" : "60px")};
  }

  @media (max-width: 950px) {
    bottom: ${props => (props.selected ? "-108px" : "80px")};
  }

  @media (max-width: 700px) {
    bottom: ${props => (props.selected ? "-108px" : "70px")};
  }

  @media (max-width: 580px) {
    bottom: ${props => (props.selected ? "-108px" : "55px")};
  }

  @media (max-width: 480px) {
    bottom: ${props => (props.selected ? "-108px" : "80px")};
  }

  @media (max-width: 340px) {
    bottom: ${props => (props.selected ? "-108px" : "70px")};
  }
`

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`
const SlideShowBox = styled.div`
  display: grid;
  margin-top: 125px;
  transform: translateX(-20px);
  width: 1200px;
  height: 600px;
  grid-template: 90% 10% / 30% 70%;
  grid-row-gap: 40px;
  animation: ${fadeIn} 1s ease;

  @media (max-width: 1350px) {
    width: 75vw;
  }

  @media (max-width: 950px) {
    width: 85vw;
    height: auto;
    margin: 100px auto;
  }

  @media (max-width: 820px) {
    display: flex;
    transform: none;
    width: 100%;
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 500px) {
    gap: 20px;
  }
`

const Description = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`

const DescriptionTxt = styled.p`
  hyphens: none;
  @media (max-width: 1280px) {
    font-size: 15px;
  }

  @media (max-width: 1088px) {
    font-size: 14px;
  }

  @media (max-width: 978px) {
    line-height: 1.2;
  }
  @media (max-width: 820px) {
    margin-bottom: 30px;
    width: 90%;
  }
`

const SlideShowImg = styled(props => <Img {...props} />)`
  width: 100%;
  height: 100%;

  @media (max-width: 820px) {
    order: -2;
    width: 90%;
  }
`

const RoundButtonsList = styled.ul`
  display: flex;
  grid-area: 2 / 2 / -1 / -1;
  justify-self: center;

  @media (max-width: 820px) {
    order: -1;
    margin: 40px 0;
  }
`

const RoundButton = styled.li`
  border-radius: 50%;
  background-color: ${props =>
    props.selected ? commonStyles.colors.darkGreen : "grey"};
  width: 30px;
  height: 30px;
  cursor: pointer;
  &:not(:last-child) {
    margin-right: 30px;
  }
  transition: background-color 0.3s ease;
`

const ModalContainer = styled.div`
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.8);
  width: 100%;
  height: 100%;
  overflow-y: scroll;
`

const CloseButton = styled.svg`
  position: absolute;
  top: 15px;
  right: 20px;
  fill: black;
  width: 40px;
  height: 40px;
  cursor: pointer;
  & > * {
    pointer-events: none;
  }
`

function Services() {
  const [selectedItem, setSelectedItem] = useState(10)
  const [imageIndex, setImageIndex] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(0)

  useEffect(() => {
    setViewportWidth(
      Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
    )
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndex(imageIndex => (imageIndex === 4 ? 0 : imageIndex + 1))
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  const data = useStaticQuery(graphql`
    query {
      carRentalOne: file(relativePath: { eq: "car-rental-1.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      carRentalTwo: file(relativePath: { eq: "car-rental-2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      carRentalThree: file(relativePath: { eq: "car-rental-3.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      carRentalFour: file(relativePath: { eq: "car-rental-4.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      carRentalFive: file(relativePath: { eq: "car-rental-5.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      packagesOne: file(relativePath: { eq: "packages-1.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      packagesTwo: file(relativePath: { eq: "packages-2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      packagesThree: file(relativePath: { eq: "packages-3.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      packagesFour: file(relativePath: { eq: "packages-4.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      packagesFive: file(relativePath: { eq: "packages-5.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      airportOne: file(relativePath: { eq: "airport-1.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      airportTwo: file(relativePath: { eq: "airport-2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      airportThree: file(relativePath: { eq: "airport-3.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      airportFour: file(relativePath: { eq: "airport-4.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      airportFive: file(relativePath: { eq: "airport-5.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      toursOne: file(relativePath: { eq: "tours-1.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      toursTwo: file(relativePath: { eq: "tours-2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      toursThree: file(relativePath: { eq: "tours-3.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      toursFour: file(relativePath: { eq: "tours-4.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      toursFive: file(relativePath: { eq: "tours-5.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)

  const images = [
    [
      {
        img: data.carRentalOne.childImageSharp.fluid,
        alt: "White sedan",
        title: "Photo by Matthew T Rader on Unsplash",
      },
      {
        img: data.carRentalTwo.childImageSharp.fluid,
        alt: "Black steering wheel",
        title: "Photo by Erik Nielsen on Unsplash",
      },
      {
        img: data.carRentalThree.childImageSharp.fluid,
        alt: "teal 3-door hatchback on road",
        title: "Photo by Oli Woodman on Unsplash",
      },
      {
        img: data.carRentalFour.childImageSharp.fluid,
        alt: "Black BMW in Rome",
        title: "Photo by Matt Atherton on Unsplash",
      },
      {
        img: data.carRentalFive.childImageSharp.fluid,
        alt: "Roadtrip driver",
        title: "Photo by Alex Jumper on Unsplash",
      },
    ],
    [
      {
        img: data.packagesOne.childImageSharp.fluid,
        alt: "Warn & Cold",
        title: "Photo by timJ on Unsplash",
      },
      {
        img: data.packagesTwo.childImageSharp.fluid,
        alt: "aerial view of person and car",
        title: "Photo by Alexandru G. STAVRICĂ on Unsplash",
      },
      {
        img: data.packagesThree.childImageSharp.fluid,
        alt: "wooden bridge during daytmie",
        title: "Photo by Ben Garratt on Unsplash",
      },
      {
        img: data.packagesFour.childImageSharp.fluid,
        alt: "Minimal",
        title: "Photo by Zoltan Tasi on Unsplash",
      },
      {
        img: data.packagesFive.childImageSharp.fluid,
        alt:
          "grayscale photography of man taking photo of mountain during daytime",
        title: "Photo by Adrian Moise on Unsplash",
      },
    ],
    [
      {
        img: data.airportOne.childImageSharp.fluid,
        alt: "white air plane wing",
        title: "Photo by Emiel Molenaar on Unsplash",
      },
      {
        img: data.airportTwo.childImageSharp.fluid,
        alt: "blue airplane interior with seats",
        title: "Photo by JC Gellidon on Unsplash",
      },
      {
        img: data.airportThree.childImageSharp.fluid,
        alt: "white airplane passing the people at the beach during daytime",
        title: "Photo by Ramon Kagie on Unsplash",
      },
      {
        img: data.airportFour.childImageSharp.fluid,
        alt: "two American Airlines planes on airport",
        title: "Photo by damian hutter on Unsplash",
      },
      {
        img: data.airportFive.childImageSharp.fluid,
        alt: "shallow focus photography of Taxi signage",
        title: "Photo by Lexi Ruskell on Unsplash",
      },
    ],
    [
      {
        img: data.toursOne.childImageSharp.fluid,
        alt: "people walking on dirt road near mountain during daytime",
        title: "Photo by Luke Porter on Unsplash",
      },
      {
        img: data.toursTwo.childImageSharp.fluid,
        alt: "woman taking photo of high-rise building beside road",
        title: "Photo by Priscilla Du Preez on Unsplash",
      },
      {
        img: data.toursThree.childImageSharp.fluid,
        alt: "two people pointing at London's number one sightseeing map",
        title: "Photo by Janis Oppliger on Unsplash",
      },
      {
        img: data.toursFour.childImageSharp.fluid,
        alt: "man holding DSLR camera beside bridge",
        title: "Photo by Alicia Steels on Unsplash",
      },
      {
        img: data.toursFive.childImageSharp.fluid,
        alt: "woman wearing backpack walking on road",
        title: "Photo by Steven Lewis on Unsplash",
      },
    ],
  ]

  const titles = [
    "Car Rental Services",
    "Tailor-Made Packages",
    "Airport Transfers",
    "Guided Tours",
  ]

  const descriptions = [
    `Guided tours over Mauritius and Rodrigues<br /><br />
  Adapted vehicle according to the number of travellers<br /><br />
  Local, experienced and trained drivers/guides<br /><br />
  Tailor-made tours upon request<br /><br />
  Pick-up and drop off at Hotel`,
    `Airport transfer to and from your accommodation and the airport<br /><br />
  VIP Transfer through VIP Lounge<br/><br />
  Possibility to cater for large group of travellers as well`,
    `Rental on unlimited mileage<br /><br />
  A car adapted to number of travellers<br/><br />
  Car with a driver upon request<br/><br />
  Replacement car upon breakdown<br/><br />
  Delivery and pick up from Airport or Hotel<br/><br />
  Price negotiable on long term rent`,
    `Amazing holidays in the most beautiful countries<br/><br />
  Travel insurance<br/><br />
  Hotel booking<br/><br />
  Cruise booking<br/><br />
  Train Booking`,
  ]

  const items = [images[0][0], images[1][0], images[2][0], images[3][0]].map(
    (item, index) => {
      return (
        <BoxItem
          onClick={() => {
            setImageIndex(0)
            setSelectedItem(selectedItem => (selectedItem < 4 ? 10 : index))
          }}
          key={`boxitem-${index}`}
        >
          <Figure>
            <FigureImg fluid={item.img} alt={item.alt} />
            <Caption
              selected={
                selectedItem === index && viewportWidth > 950 ? true : false
              }
            >
              {titles[index]}
            </Caption>
          </Figure>
          <Overlay
            selected={
              selectedItem === index && viewportWidth > 950 ? true : false
            }
          />
        </BoxItem>
      )
    }
  )

  const roundButtonsJsx = []

  for (let i = 0; i < 5; i++) {
    roundButtonsJsx.push(
      <RoundButton
        onClick={() => setImageIndex(i)}
        selected={imageIndex === i ? true : false}
        key={`roundbutton-${i}`}
      ></RoundButton>
    )
  }
  return (
    <>
      <SEO title="Our services" />
      <Layout>
        <div>
          {selectedItem < 4 && viewportWidth < 950 ? (
            <ModalContainer>
              <CloseButton onClick={() => setSelectedItem(10)}>
                <use xlinkHref={`${icons}#icon-close`} />
              </CloseButton>
              <SlideShowBox>
                <Description>
                  <DescriptionTxt
                    dangerouslySetInnerHTML={{
                      __html: descriptions[selectedItem],
                    }}
                  />
                  <ButtonLink to="/contact">Contact us!</ButtonLink>
                </Description>
                <SlideShowImg fluid={images[selectedItem][imageIndex].img} />
                <RoundButtonsList>{roundButtonsJsx}</RoundButtonsList>
              </SlideShowBox>
            </ModalContainer>
          ) : (
            ""
          )}
        </div>
        <Navbar
          fixed={false}
          hidden={false}
          variant="generic"
          page="services"
        />
        <main>
          <Section active={selectedItem > 3 ? false : true}>
            <SectionHeading color="grey">We are best at:</SectionHeading>
            <div style={{ marginBottom: "50px" }}></div>
            <BoxList>{items}</BoxList>
            {selectedItem < 4 && viewportWidth > 950 ? (
              <SlideShowBox>
                <Description>
                  <DescriptionTxt
                    dangerouslySetInnerHTML={{
                      __html: descriptions[selectedItem],
                    }}
                  />
                  <ButtonLink to="/contact">Contact us!</ButtonLink>
                </Description>
                <SlideShowImg fluid={images[selectedItem][imageIndex].img} />
                <RoundButtonsList>{roundButtonsJsx}</RoundButtonsList>
              </SlideShowBox>
            ) : (
              <ButtonLink to="/contact">Contact us!</ButtonLink>
            )}
          </Section>
        </main>
      </Layout>
    </>
  )
}

export default Services
